import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Chip from 'shared/atoms/Chip';
import CircularSpinner from 'shared/atoms/CircularSpinner';

import Table, { CELL_TYPES, ORDER_TYPES } from 'shared/organisms/Table';

import { courseStatus } from 'shared/const';
import theme from 'shared/themes/default';

import { useWatchData } from '../../Context';
import { listPlaylistUserWatchData } from '../../Context/graphql/queries';

const SpinnerWrapper = styled.div`
  padding-top: ${theme.spacing.m2};
`;

const InfoWrapper = styled.div`
  align-items: center;
  display: flex;
  gap: ${theme.spacing.s};
`;

const PlaylistWatchDataTableRowItem = ({ item, open, playlistId }) => {
  const { t } = useTranslation();

  const { organizationSelectedId, setWatchDataError, useLazyGraphQLQuery } =
    useWatchData();

  const [listPlaylistUserWatchDataQuery, listPlaylistUserWatchDataResponse] =
    useLazyGraphQLQuery(listPlaylistUserWatchData);

  const watchHistory =
    listPlaylistUserWatchDataResponse.data?.listPlaylistUserWatchData || [];

  useEffect(() => {
    const { error } = listPlaylistUserWatchDataResponse;

    if (error) {
      setWatchDataError(true);
    }
  }, [listPlaylistUserWatchDataResponse]);

  useEffect(() => {
    if (open) {
      listPlaylistUserWatchDataQuery({
        variables: {
          organizationId: organizationSelectedId,
          playlistId,
          userId: item.userId,
        },
      });
    }
  }, [open]);

  const columns = [
    {
      key: 'name',
      label: t('watchData.courses'),
      expandedSticky: true,
      fixedWidth: '43%',
      type: CELL_TYPES.COMPONENT,
      render: (row) => (
        <InfoWrapper>
          {row.name}
          {row.status === courseStatus.ARCHIVED && (
            <Chip
              color={theme.palette.errorLight}
              label={t('watchData.deleted')}
              small
              theme="dark"
            />
          )}
        </InfoWrapper>
      ),
    },
    {
      key: 'progress',
      label: t('watchData.progress'),
      type: CELL_TYPES.COMPONENT,
      render: (row) => `${row.progress}%`,
    },
    {
      key: 'firstTimeWatched',
      label: t('watchData.courseStart'),
      type: CELL_TYPES.DATE,
    },
    {
      key: 'completedTime',
      label: t('watchData.completionDate'),
      type: CELL_TYPES.DATE,
    },
    {
      key: 'certificationTime',
      label: t('watchData.certificationDate'),
      type: CELL_TYPES.DATE,
    },
  ];

  return listPlaylistUserWatchDataResponse.loading ? (
    <SpinnerWrapper>
      <CircularSpinner static />
    </SpinnerWrapper>
  ) : (
    watchHistory.length > 0 && (
      <Table
        ariaLabel="Expandable watch data row inner content"
        columns={columns}
        items={watchHistory}
        keyField="id"
        defaultOrder={ORDER_TYPES.ASC}
        defaultOrderBy="name"
        isExpandedType
        isExpandedOpen={open}
      />
    )
  );
};

PlaylistWatchDataTableRowItem.propTypes = {
  item: PropTypes.object.isRequired,
  playlistId: PropTypes.string.isRequired,
  open: PropTypes.bool,
};

PlaylistWatchDataTableRowItem.defaultProps = {
  open: false,
};

export default PlaylistWatchDataTableRowItem;
